import api from "./api";

const SUBSCRIPTION_STORAGE_KEY = "pc_web_push_subscription_id";
let registeredToken = null;

function runtimeWindow(overrides = {}) {
  return overrides.windowObj || (typeof window !== "undefined" ? window : undefined);
}

function runtimeNavigator(overrides = {}) {
  return overrides.navigatorObj || (typeof navigator !== "undefined" ? navigator : undefined);
}

function runtimeNotification(overrides = {}) {
  return overrides.NotificationApi || (typeof Notification !== "undefined" ? Notification : undefined);
}

function runtimeStorage(overrides = {}) {
  return overrides.storage || runtimeWindow(overrides)?.localStorage;
}

function runtimeApi(overrides = {}) {
  return overrides.apiClient || api;
}

export function isIosDevice(navigatorObj = runtimeNavigator()) {
  if (!navigatorObj) return false;
  const userAgent = navigatorObj.userAgent || "";
  return /iPad|iPhone|iPod/.test(userAgent)
    || (navigatorObj.platform === "MacIntel" && Number(navigatorObj.maxTouchPoints || 0) > 1);
}

export function isStandaloneApp(windowObj = runtimeWindow(), navigatorObj = runtimeNavigator()) {
  return Boolean(
    navigatorObj?.standalone
    || windowObj?.matchMedia?.("(display-mode: standalone)")?.matches,
  );
}

export function supportsWebPush(overrides = {}) {
  const navigatorObj = runtimeNavigator(overrides);
  const NotificationApi = runtimeNotification(overrides);
  const windowObj = runtimeWindow(overrides);
  return Boolean(
    windowObj?.isSecureContext
    && navigatorObj?.serviceWorker
    && windowObj?.PushManager
    && NotificationApi,
  );
}

export async function registerPlayerServiceWorker(overrides = {}) {
  const navigatorObj = runtimeNavigator(overrides);
  if (!navigatorObj?.serviceWorker?.register) return null;
  return navigatorObj.serviceWorker.register("/service-worker.js", {
    scope: "/",
    updateViaCache: "none",
  });
}

function applicationServerKey(value, decoder = typeof atob === "function" ? atob : null) {
  if (!decoder) throw new Error("base64_decoder_unavailable");
  const padded = `${value}${"=".repeat((4 - (value.length % 4)) % 4)}`
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const decoded = decoder(padded);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

function subscriptionPayload(subscription, overrides = {}) {
  const serialized = subscription.toJSON();
  return {
    endpoint: serialized.endpoint || subscription.endpoint,
    expiration_time: serialized.expirationTime ?? subscription.expirationTime ?? null,
    keys: {
      p256dh: serialized.keys?.p256dh,
      auth: serialized.keys?.auth,
    },
    platform: isIosDevice(runtimeNavigator(overrides)) ? "ios" : "web",
  };
}

function saveSubscriptionId(id, overrides = {}) {
  if (id) runtimeStorage(overrides)?.setItem(SUBSCRIPTION_STORAGE_KEY, id);
}

function clearSubscriptionId(overrides = {}) {
  runtimeStorage(overrides)?.removeItem(SUBSCRIPTION_STORAGE_KEY);
}

async function registerSubscription(subscription, overrides = {}) {
  const { data } = await runtimeApi(overrides).post(
    "/push/subscriptions",
    subscriptionPayload(subscription, overrides),
  );
  saveSubscriptionId(data.subscription_id, overrides);
  return data;
}

export async function webPushStatus(overrides = {}) {
  const windowObj = runtimeWindow(overrides);
  const navigatorObj = runtimeNavigator(overrides);
  const NotificationApi = runtimeNotification(overrides);
  if (isIosDevice(navigatorObj) && !isStandaloneApp(windowObj, navigatorObj)) {
    return { status: "home_screen_required", permission: NotificationApi?.permission || "default" };
  }
  if (!supportsWebPush(overrides)) return { status: "unsupported" };

  const { data: server } = await runtimeApi(overrides).get("/push/status");
  if (!server.configured || !server.public_key) return { status: "server_unavailable" };
  if (NotificationApi.permission === "denied") return { status: "denied", permission: "denied" };

  const registration = await registerPlayerServiceWorker(overrides);
  const subscription = await registration.pushManager.getSubscription();
  if (NotificationApi.permission === "granted" && subscription) {
    await registerSubscription(subscription, overrides);
    return { status: "active", permission: "granted" };
  }
  return { status: "inactive", permission: NotificationApi.permission };
}

export async function enableWebPush(overrides = {}) {
  const windowObj = runtimeWindow(overrides);
  const navigatorObj = runtimeNavigator(overrides);
  const NotificationApi = runtimeNotification(overrides);
  if (isIosDevice(navigatorObj) && !isStandaloneApp(windowObj, navigatorObj)) {
    return { status: "home_screen_required" };
  }
  if (!supportsWebPush(overrides)) return { status: "unsupported" };

  const { data: server } = await runtimeApi(overrides).get("/push/status");
  if (!server.configured || !server.public_key) return { status: "server_unavailable" };

  let permission = NotificationApi.permission;
  if (permission === "default") permission = await NotificationApi.requestPermission();
  if (permission !== "granted") return { status: permission === "denied" ? "denied" : "inactive" };

  const registration = await registerPlayerServiceWorker(overrides);
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: applicationServerKey(server.public_key, overrides.atob),
    });
  }
  await registerSubscription(subscription, overrides);
  return { status: "active", permission: "granted" };
}

export async function syncExistingWebPushAssociation(overrides = {}) {
  const navigatorObj = runtimeNavigator(overrides);
  const NotificationApi = runtimeNotification(overrides);
  if (!supportsWebPush(overrides) || NotificationApi.permission !== "granted") {
    return { status: "unchanged" };
  }
  const { data: server } = await runtimeApi(overrides).get("/push/status");
  if (!server.configured) return { status: "unchanged" };
  const registration = await registerPlayerServiceWorker(overrides);
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription || (isIosDevice(navigatorObj) && !isStandaloneApp(runtimeWindow(overrides), navigatorObj))) {
    return { status: "unchanged" };
  }
  await registerSubscription(subscription, overrides);
  return { status: "active" };
}

export async function disableWebPush(overrides = {}) {
  const registration = supportsWebPush(overrides)
    ? await registerPlayerServiceWorker(overrides)
    : null;
  const subscription = await registration?.pushManager?.getSubscription?.();
  let subscriptionId = runtimeStorage(overrides)?.getItem(SUBSCRIPTION_STORAGE_KEY);
  if (!subscriptionId && subscription) {
    try {
      const result = await registerSubscription(subscription, overrides);
      subscriptionId = result.subscription_id;
    } catch { /* Local unsubscribe remains useful if backend is temporarily unavailable. */ }
  }
  if (subscriptionId) {
    await runtimeApi(overrides).delete(`/push/subscriptions/${encodeURIComponent(subscriptionId)}`);
  }
  await subscription?.unsubscribe?.();
  clearSubscriptionId(overrides);
  return { status: "inactive" };
}

export async function detachWebPushAssociation(overrides = {}) {
  const subscriptionId = runtimeStorage(overrides)?.getItem(SUBSCRIPTION_STORAGE_KEY);
  if (subscriptionId) {
    try {
      await runtimeApi(overrides).delete(`/push/subscriptions/${encodeURIComponent(subscriptionId)}`);
    } catch { /* Logout must continue even when the delivery API is unavailable. */ }
  }
  clearSubscriptionId(overrides);
}

export async function registerRuntimePushToken() {
  const bridge = typeof window !== "undefined" ? window.PerformanceCarePush : null;
  if (!bridge || typeof bridge.getToken !== "function") return { status: "unavailable" };
  const result = await bridge.getToken();
  const token = typeof result === "string" ? result : result?.token;
  if (!token) return { status: "unavailable" };
  const platform = typeof result === "object" && ["ios", "android", "web"].includes(result.platform)
    ? result.platform : "web";
  await api.post("/me/device-tokens", {
    token, platform, client_type: typeof result === "object" ? result.clientType || result.client_type || null : null,
  });
  registeredToken = token;
  return { status: "registered" };
}

export async function unregisterRuntimePushToken() {
  await detachWebPushAssociation();
  if (!registeredToken) return;
  const token = registeredToken;
  registeredToken = null;
  try { await api.delete(`/me/device-tokens/${encodeURIComponent(token)}`); } catch {}
}
